import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { db } from "../firebase";
import {
    collection,
    onSnapshot,
    collectionGroup,
    query,
    orderBy,
    where,
    doc,
    updateDoc,
    deleteDoc,
    getDocs,
    Timestamp,
} from "firebase/firestore";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
} from "../components/ui/select";
import CreateBookingForm from "./create_booking";
import {
    assignProvider,
    unassignProvider,
    setBookingStatus,
    deleteBooking,
    setCommissionOverride,
    bookingServices,
    computeBookingProviderAmount,
} from "../lib/bookingSync";
import CommissionOverrideFields, {
    formatCommission,
} from "../components/CommissionOverrideFields";

// Bookings (and their provider copies) are listened to for this many days at a
// time; "Load older" widens the window by the same amount.
const WINDOW_STEP_DAYS = 30;

const errorMessage = (err) => err?.message || "Unknown error";

const BookingStatusSelect = ({ order }) => {
    const [status, setStatus] = useState(order.status);

    useEffect(() => {
        setStatus(order.status);
    }, [order.status]);

    const updateStatus = async (newStatus) => {
        const previous = status;
        setStatus(newStatus);
        try {
            if (order.type === "bookings") {
                // Booking + provider copy are written in one batch.
                await setBookingStatus(order, newStatus);
            } else {
                await updateDoc(doc(db, order.type, order.id), {
                    status: newStatus,
                });
            }
        } catch (err) {
            console.error("Error updating status:", err);
            setStatus(previous);
            alert(`Failed to update status: ${errorMessage(err)}`);
        }
    };

    return (
        <Select value={status} onValueChange={updateStatus}>
            <SelectTrigger>{status}</SelectTrigger>
            <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Accepted</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="canceled">Rejected</SelectItem>
            </SelectContent>
        </Select>
    );
};

// Rows for CommissionOverrideFields: each of the booking's services with the
// provider's default commission for that category.
const overrideRows = (order, provider) =>
    bookingServices(order).map((s) => ({
        ...s,
        defaultEntry: provider?.categories?.[s.categoryId],
    }));

// Previews the provider amount; null when it can't be computed yet (e.g. a
// half-typed override value).
const previewAmount = (order, provider, overrides) => {
    if (!provider) return null;
    try {
        const valid = Object.values(overrides || {}).every(
            (o) => o.commissionValue !== "" && !isNaN(Number(o.commissionValue))
        );
        return valid ? computeBookingProviderAmount(order, provider, overrides) : null;
    } catch {
        return null;
    }
};

// Lets the admin (re)assign the provider on an existing booking. Only active
// providers that work in all of this booking's services (primary and, if set,
// secondary) are offered; those whose ServiceCoverage also matches the
// booking's pincode are suggested first. Picking a provider opens a confirm
// panel where the commission can be overridden per service before assigning.
// Assignment writes the booking and the provider's copy in one batch.
const ProviderAssignSelect = ({ order, providers, serviceCoverage }) => {
    // Provider picked in the dropdown but not yet assigned.
    const [pendingId, setPendingId] = useState("");
    const [overrides, setOverrides] = useState({});
    const [saving, setSaving] = useState(false);

    const coverageProviderIds = useMemo(() => {
        return new Set(
            serviceCoverage
                .filter(
                    (c) =>
                        c.active !== false &&
                        c.pincode === order.pincode &&
                        c.categoryId === order.categoryId
                )
                .map((c) => c.providerId)
        );
    }, [serviceCoverage, order.pincode, order.categoryId]);

    // A booking with a secondary service needs a provider who has both.
    const eligible = providers.filter(
        (p) =>
            p.active !== false &&
            p.categoryIds?.includes(order.categoryId) &&
            (!order.secondaryCategoryId ||
                p.categoryIds?.includes(order.secondaryCategoryId))
    );
    const suggested = eligible.filter((p) => coverageProviderIds.has(p.id));
    const suggestedIds = new Set(suggested.map((p) => p.id));
    const others = eligible.filter((p) => !suggestedIds.has(p.id));

    const pendingProvider = providers.find((p) => p.id === pendingId);
    const selectedId = pendingId || order.serviceProviderId || "";

    const cancel = () => {
        setPendingId("");
        setOverrides({});
    };

    const handleChange = async (newProviderId) => {
        if (newProviderId) {
            if (newProviderId === order.serviceProviderId) return cancel();
            setPendingId(newProviderId);
            setOverrides({});
            return;
        }
        // Unassign straight away — nothing to configure.
        cancel();
        if (!order.serviceProviderId) return;
        setSaving(true);
        try {
            await unassignProvider(order);
        } catch (err) {
            console.error("Error unassigning provider:", err);
            alert(`Failed to update provider: ${errorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    };

    const confirmAssign = async () => {
        setSaving(true);
        try {
            await assignProvider(order, { id: pendingId }, overrides);
            cancel();
        } catch (err) {
            console.error("Error assigning provider:", err);
            alert(`Failed to assign provider: ${errorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    };

    const preview = previewAmount(order, pendingProvider, overrides);

    return (
        <div className="flex flex-col gap-1">
            <Select value={selectedId} onValueChange={handleChange}>
                <SelectTrigger>
                    {saving
                        ? "Saving…"
                        : providers.find((p) => p.id === selectedId)?.name ||
                        order.serviceProviderName ||
                        "Unassigned"}
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="">Unassigned</SelectItem>
                    {suggested.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                            ⭐ {p.name} ({p.phone_number || "no phone"})
                        </SelectItem>
                    ))}
                    {others.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                            {p.name} ({p.phone_number || "no phone"})
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <span className="text-xs text-gray-500">
                {order.secondaryCategoryId
                    ? `Only providers who do both ${order.categoryName} and ${order.secondaryCategoryName} are listed. `
                    : ""}
                {suggested.length > 0
                    ? `⭐ = covers pincode ${order.pincode || "—"} for this service`
                    : `No provider covers pincode ${order.pincode || "—"} for this service yet — showing all active providers for this service.`}
            </span>

            {pendingProvider && (
                <div className="mt-1 space-y-2 rounded border border-blue-200 bg-blue-50 p-3">
                    <p className="text-sm font-medium text-gray-800">
                        Assign {pendingProvider.name}
                    </p>
                    <CommissionOverrideFields
                        services={overrideRows(order, pendingProvider)}
                        value={overrides}
                        onChange={setOverrides}
                        disabled={saving}
                    />
                    <p className="text-sm text-gray-700">
                        Provider amount: {preview === null ? "—" : `₹${preview}`}
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={saving}
                            onClick={confirmAssign}
                            className="rounded bg-blue-600 px-3 py-1 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? "Assigning…" : "Assign"}
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={cancel}
                            className="rounded border px-3 py-1 text-sm hover:bg-white disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Shows the payout the assigned provider receives for this booking, read from
// the provider's copy (joined by bookingId from the collection-group listener
// in the parent). providerAmount is frozen when the booking is assigned; the
// admin can override the commission per service until it's paid.
const ProviderPayoutInfo = ({ order, copy, providers }) => {
    const [editing, setEditing] = useState(false);
    const [overrides, setOverrides] = useState({});
    const [saving, setSaving] = useState(false);

    if (!order.serviceProviderId) return null;

    if (!copy) {
        return (
            <div>
                <strong>Provider Payout:</strong> —
            </div>
        );
    }

    const provider = providers.find((p) => p.id === order.serviceProviderId);
    const current = copy.commissionOverride || {};
    const services = overrideRows(order, provider);
    const overridden = services.filter((s) => current[s.categoryId]);
    const paid = copy.payoutStatus === "paid";

    const startEditing = () => {
        const initial = {};
        overridden.forEach((s) => {
            const o = current[s.categoryId];
            initial[s.categoryId] = {
                commissionType: o.commissionType,
                commissionValue: String(o.commissionValue),
            };
        });
        setOverrides(initial);
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await setCommissionOverride(order, copy, overrides);
            setEditing(false);
        } catch (err) {
            console.error("Error overriding commission:", err);
            alert(`Failed to update commission: ${errorMessage(err)}`);
        } finally {
            setSaving(false);
        }
    };

    const preview = editing ? previewAmount(order, provider, overrides) : null;

    return (
        <div className="space-y-1">
            <div>
                <strong>Provider Payout:</strong> ₹{copy.providerAmount}{" "}
                <span className="text-xs text-gray-500">
                    ({copy.payoutStatus || "pending"})
                </span>
                {overridden.map((s) => (
                    <span
                        key={s.categoryId}
                        className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                    >
                        {s.name}: custom {formatCommission(current[s.categoryId])}
                    </span>
                ))}
                {!paid && !editing && provider && (
                    <button
                        type="button"
                        onClick={startEditing}
                        className="ml-2 text-xs text-blue-600 hover:underline"
                    >
                        {overridden.length ? "Edit cut" : "Override cut"}
                    </button>
                )}
            </div>

            {editing && (
                <div className="space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                    <CommissionOverrideFields
                        services={services}
                        value={overrides}
                        onChange={setOverrides}
                        disabled={saving}
                    />
                    <p className="text-sm text-gray-700">
                        Provider amount: {preview === null ? "—" : `₹${preview}`}
                    </p>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={saving}
                            onClick={save}
                            className="rounded bg-blue-600 px-3 py-1 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            onClick={() => setEditing(false)}
                            className="rounded border px-3 py-1 text-sm hover:bg-white disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                    <span className="block text-xs text-gray-500">
                        Untick a service to go back to the provider's default. Applies to
                        this booking only; reassigning resets it.
                    </span>
                </div>
            )}
        </div>
    );
};

const CombinedDashboard = () => {
    const [allOrders, setAllOrders] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [providers, setProviders] = useState([]);
    const [serviceCoverage, setServiceCoverage] = useState([]);
    // bookingId -> provider copy (providerAmount / payoutStatus).
    const [copiesByBookingId, setCopiesByBookingId] = useState({});
    const [windowDays, setWindowDays] = useState(WINDOW_STEP_DAYS);
    const firstLoadRef = useRef(true); // skip alarm on initial load
    const audioRef = useRef(null); // persist audio object
    // Booking ids created from this browser this session — used to skip the
    // alarm when their snapshot "added" event arrives.
    const locallyCreatedIdsRef = useRef(new Set());
    const [alarmEnabled, setAlarmEnabled] = useState(
        () => localStorage.getItem("bookingAlarmEnabled") !== "false"
    );
    // Ref so the snapshot listener sees the latest value without resubscribing.
    const alarmEnabledRef = useRef(alarmEnabled);

    const toggleAlarm = () => {
        const next = !alarmEnabled;
        alarmEnabledRef.current = next;
        localStorage.setItem("bookingAlarmEnabled", String(next));
        setAlarmEnabled(next);
        if (!next && audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
    };

    // Loaded once — feeds the provider-assignment dropdown on each booking
    // card. Mirrors create_booking.jsx's one-time fetch of ServiceProviders.
    useEffect(() => {
        const fetchAssignmentData = async () => {
            try {
                const [providersSnap, coverageSnap] = await Promise.all([
                    getDocs(collection(db, "ServiceProviders")),
                    getDocs(collection(db, "ServiceCoverage")),
                ]);
                setProviders(providersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
                setServiceCoverage(coverageSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error("Error loading providers / service coverage:", err);
            }
        };
        fetchAssignmentData();
    }, []);

    const playSound = useCallback(() => {
        audioRef.current = new Audio("/notification.mp3");
        audioRef.current.play().catch((err) =>
            console.log("Autoplay blocked until interaction", err)
        );
    }, []);

    const stopSound = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
    }, []);

    // Replaces all orders of `type` with the snapshot's docs; rings the alarm
    // for newly added ones unless `skipAlarm` (initial load of a listener).
    const processSnapshot = useCallback(
        (snapshot, type, skipAlarm) => {
            const docs = snapshot.docs.map((doc) => ({
                id: doc.id,
                type,
                ...doc.data(),
                createdAt:
                    type === "bookings"
                        ? doc.data().dateTime?.toDate?.() ?? new Date()
                        : doc.data().createdAt?.toDate?.() ?? new Date(),
            }));

            if (!skipAlarm) {
                snapshot.docChanges().forEach((change) => {
                    if (change.type !== "added") return;
                    // Skip the alarm for bookings created from this browser.
                    if (locallyCreatedIdsRef.current.has(change.doc.id)) {
                        locallyCreatedIdsRef.current.delete(change.doc.id);
                        return;
                    }
                    if (alarmEnabledRef.current) playSound();
                });
            }

            setAllOrders((prev) => {
                const others = prev.filter((o) => o.type !== type);
                return [...others, ...docs].sort(
                    (a, b) => b.createdAt - a.createdAt
                );
            });
        },
        [playSound]
    );

    useEffect(() => {
        // Stop sound on any click
        const handleClick = () => stopSound();
        document.addEventListener("click", handleClick);

        const groceryQuery = query(
            collection(db, "groceryOrders"),
            orderBy("createdAt", "desc")
        );

        const unsubGroceries = onSnapshot(groceryQuery, (snapshot) =>
            processSnapshot(snapshot, "groceryOrders", firstLoadRef.current)
        );

        const enableNotificationsTimeout = setTimeout(
            () => (firstLoadRef.current = false),
            1000
        );

        return () => {
            unsubGroceries();
            clearTimeout(enableNotificationsTimeout);
            document.removeEventListener("click", handleClick);
            stopSound();
        };
    }, [processSnapshot, stopSound]);

    // Bookings and the provider copies share one date window (last N days,
    // widened by "Load older") so the page doesn't re-read every booking ever.
    // The copies come from a single collection-group listener, joined to the
    // bookings by bookingId in memory.
    useEffect(() => {
        const since = Timestamp.fromDate(
            new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000)
        );

        let initialBookings = true; // skip alarm for this listener's first load
        const unsubBookings = onSnapshot(
            query(
                collection(db, "bookings"),
                where("dateTime", ">=", since),
                orderBy("dateTime", "desc")
            ),
            (snapshot) => {
                processSnapshot(snapshot, "bookings", initialBookings);
                initialBookings = false;
            },
            (err) => console.error("Error listening to bookings:", err)
        );

        const unsubCopies = onSnapshot(
            query(
                collectionGroup(db, "assignedBookings"),
                where("dateTime", ">=", since)
            ),
            (snapshot) => {
                const map = {};
                snapshot.docs.forEach((d) => {
                    map[d.data().bookingId || d.id] = d.data();
                });
                setCopiesByBookingId(map);
            },
            (err) => console.error("Error listening to provider copies:", err)
        );

        return () => {
            unsubBookings();
            unsubCopies();
        };
    }, [windowDays, processSnapshot]);

    const deleteOrder = async (order) => {
        try {
            if (order.type === "bookings") {
                // Booking + provider copy are deleted in one batch.
                await deleteBooking(order);
            } else {
                await deleteDoc(doc(db, order.type, order.id));
            }
            setAllOrders((prev) =>
                prev.filter((o) => !(o.id === order.id && o.type === order.type))
            );
        } catch (err) {
            console.error("Error deleting order:", err);
            alert(`Failed to delete: ${errorMessage(err)}`);
        }
    };

    const markComplete = async (order) => {
        try {
            await setBookingStatus(order, "completed");
        } catch (err) {
            console.error("Error completing booking:", err);
            alert(`Failed to mark complete: ${errorMessage(err)}`);
        }
    };

    return (
        <div className="h-screen overflow-y-auto p-4 bg-gray-50 space-y-4">
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-3xl font-bold">All Orders</h1>
                <div className="flex items-center gap-3">
                    <button
                        onClick={toggleAlarm}
                        className={`rounded px-4 py-2 font-semibold text-white ${
                            alarmEnabled
                                ? "bg-green-600 hover:bg-green-700"
                                : "bg-gray-500 hover:bg-gray-600"
                        }`}
                    >
                        {alarmEnabled ? "🔔 Alarm On" : "🔕 Alarm Off"}
                    </button>
                    <button
                        onClick={() => setShowCreateForm((prev) => !prev)}
                        className="rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
                    >
                        {showCreateForm ? "Close" : "+ Create Booking"}
                    </button>
                </div>
            </div>

            {showCreateForm && (
                <CreateBookingForm
                    // Register the id BEFORE the write — Firestore's local
                    // snapshot fires the "added" event (latency compensation)
                    // before setDoc resolves, so registering in onCreated is
                    // too late and the alarm still rings.
                    onBeforeCreate={(bookingId) => {
                        if (bookingId) {
                            locallyCreatedIdsRef.current.add(bookingId);
                        }
                    }}
                    onCreated={() => setShowCreateForm(false)}
                />
            )}

            {allOrders.map((order) => (
                <Card key={`${order.type}-${order.id}`}>
                    <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                            <strong>Type:</strong>{" "}
                            {order.type === "bookings"
                                ? "Service Booking"
                                : "Grocery Order"}
                        </div>
                        <div>
                            <strong>Status:</strong>{" "}
                            <BookingStatusSelect order={order} />
                        </div>
                        <div><strong>Date:</strong> {order.createdAt.toLocaleString()}</div>

                        {order.type === "bookings" && (
                            <>
                                <div>
                                    <strong>User:</strong> {order.userName} ({order.userPhoneNumber})
                                </div>
                                <div>
                                    <strong>Provider:</strong>{" "}
                                    <ProviderAssignSelect
                                        order={order}
                                        providers={providers}
                                        serviceCoverage={serviceCoverage}
                                    />
                                </div>
                                <div>
                                    <strong>Primary Service:</strong> {order.categoryName}
                                </div>

                                {order.secondaryCategoryName && (
                                    <div>
                                        <strong>Secondary Service:</strong> {order.secondaryCategoryName}
                                    </div>
                                )}

                                {order.secondaryServiceOptionTitle && (
                                    <div>
                                        <strong>Secondary Option:</strong> {order.secondaryServiceOptionTitle}
                                    </div>
                                )}

                                <div>
                                    <strong>Description:</strong> {order.problemDescription}
                                </div>

                                {order.payableAmount !== undefined && (
                                    <div>
                                        <strong>Total Payable:</strong> ₹{order.payableAmount}
                                    </div>
                                )}

                                <ProviderPayoutInfo
                                    order={order}
                                    copy={copiesByBookingId[order.id]}
                                    providers={providers}
                                />

                                <div className="md:col-span-2">
                                    <strong>Address:</strong> {order.address}
                                </div>

                            </>
                        )}

                        {order.type === "groceryOrders" && (
                            <>
                                <div><strong>UserId:</strong> {order.userId}</div>
                                <div><strong>Phone:</strong> {order.phoneNumber}</div>
                                <div><strong>Address:</strong> {order.address}</div>
                                <div><strong>Total Price:</strong> ₹{order.totalPrice}</div>
                                <div><strong>Delivery:</strong> ₹{order.deliveryCharges}</div>
                                <div><strong>Grand Total:</strong> ₹{order.grandTotal}</div>
                                <div className="col-span-2">
                                    <strong>Items:</strong>
                                    <ul className="list-disc ml-6">
                                        {order.items?.map((item, idx) => (
                                            <li key={idx}>
                                                {item.name} ({item.unit || "pcs"}) - {item.quantity} × ₹{item.price}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}

                        <div className="col-span-1 md:col-span-2 flex gap-2">
                            {order.type === "bookings" && order.status !== "completed" && (
                                <Button
                                    onClick={() => markComplete(order)}
                                >
                                    Mark Complete
                                </Button>
                            )}
                            <Button
                                variant="destructive"
                                onClick={() => deleteOrder(order)}
                            >
                                Delete
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ))}

            <div className="flex justify-center pb-4">
                <Button onClick={() => setWindowDays((d) => d + WINDOW_STEP_DAYS)}>
                    Load older (showing last {windowDays} days)
                </Button>
            </div>
        </div>
    );
};

export default CombinedDashboard;
